import React, { memo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { personType } from '@reducers/conference';
import { Td, Name, Details } from '@layout/Attendees';
import { callAttendee } from '@actions/conferences';
import styled from 'styled-components';
import colors from '@theme/colors';
import { CONFERENCE_STATUSES } from '@config/statuses-constants';
import { PERSON_TYPES } from '@config/authentication';
import { Icon, InvisibleButton } from '@atoms';

const details = (person) =>
  [
    person.phoneNumber,
    `${person.country || ''}${person.timezone ? ` (UTC ${person.timezone})` : ''}`,
    person.email,
  ]
    .filter((item) => !!item)
    .join(', ');

const conferenceNumber = ({ countries, person }) => {
  const numberById = countries.find(({ id }) => id === person.conferencePhoneNumberId);
  if (numberById) return numberById.number;

  const numberByCountry = countries.find(({ country }) => country === person.country);
  if (numberByCountry) return numberByCountry.number;

  return 'Not Available';
};

const NameWrapper = styled.div`
  white-space: nowrap;

  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 10px;
    background-color: ${({ status }) => {
      switch (status) {
        case CONFERENCE_STATUSES.TALKING:
          return colors.SUCCESS;
        default:
          return colors.LIGHT_GREY;
      }
    }};
  }
`;

export const Attendees = ({
  people,
  countries,
  showTranscripts,
  callAttendee,
  onlineParticipantIds = [],
  offlineParticipantNumbers = [],
  isAttendeeCallDisabled,
  requestTranscriptCompletionEmail,
  canRequestTranscriptCompletionEmail,
  transcriptId,
}) => {
  const handleEmailSendButtonClick = (person) => {
    const verb = person.isTranscriptionCompletedEmailSent ? 're-send' : 'send';
    const prompt = window.confirm(
      `Are sure to ${verb} transcription email to ${person.name}`,
    );
    if (prompt) {
      requestTranscriptCompletionEmail(person.id, transcriptId);
    }
  };

  return (
    <table>
      <tbody>
        {people.map((person) => (
          <tr key={person.id}>
            <Td>{person.type}</Td>
            <Td>
              <Name>
                <NameWrapper
                  status={
                    onlineParticipantIds.includes(person.id)
                      ? CONFERENCE_STATUSES.TALKING
                      : null
                  }
                >
                  {person.name}
                </NameWrapper>
                {person.id}
                {!showTranscripts && person.phoneNumber && !isAttendeeCallDisabled && (
                  <InvisibleButton
                    id={`call-attendee-${person.id}`}
                    active={!person.called}
                    onClick={() => !person.called && callAttendee(person)}
                    title={!person.called ? `Call ${person.name}` : null}
                  >
                    <Icon name={`cellPhone${person.called ? '' : 'Active'}`} />
                  </InvisibleButton>
                )}
                {showTranscripts &&
                  person.type === PERSON_TYPES.CLIENT &&
                  canRequestTranscriptCompletionEmail && (
                    <InvisibleButton
                      id={`send-email-to-attendee-${person.id}`}
                      active
                      onClick={() => handleEmailSendButtonClick(person)}
                      title={`${
                        person.isTranscriptionCompletedEmailSent ? 'Re-send' : 'Send'
                      } email about transcript completion to ${person.name}`}
                    >
                      <Icon name="mail" />
                    </InvisibleButton>
                  )}
              </Name>
              <Details>{details(person)}</Details>
              {!showTranscripts && (
                <Details>
                  Conference number: {conferenceNumber({ person, countries })}
                </Details>
              )}
            </Td>
          </tr>
        ))}
        {offlineParticipantNumbers.map((prticipant) => (
          <tr key={prticipant}>
            <Td />
            <Td>
              <Name>
                <NameWrapper status={CONFERENCE_STATUSES.TALKING}>
                  {prticipant}
                </NameWrapper>
              </Name>
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

Attendees.propTypes = {
  people: PropTypes.arrayOf(personType),
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      country: PropTypes.string.isRequired,
      number: PropTypes.string.isRequired,
    }),
  ),
};

Attendees.defaultProps = {
  people: [],
  countries: [],
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = {
  callAttendee,
};

export default memo(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(Attendees),
);



// WEBPACK FOOTER //
// ./components/atoms/Attendees.js